import { GridCellKind } from '@glideapps/glide-data-grid';
import type { CustomCell, CustomRenderer } from '@glideapps/glide-data-grid';
import { drawTextCell } from '@glideapps/glide-data-grid';
import { type Chip, paintChips } from './chipPainting.js';
import { PicklistEditor, type PicklistOption } from './PicklistEditor.js';

export interface SinglePicklistData {
  readonly kind: 'singlePicklist';
  readonly value: string;
  readonly options: readonly PicklistOption[];
  /**
   * A value a feed observed, shown after the chosen one and belonging to
   * nobody: the editor never sees it, so it is never what a commit writes.
   */
  readonly observed?: Chip | undefined;
}

export type SinglePicklistCell = CustomCell<SinglePicklistData>;

function labelOf(data: SinglePicklistData): string {
  // Nothing chosen reads as an empty cell, even where the editor offers a
  // labelled way back to nothing.
  if (data.value === '') return '';
  return data.options.find((option) => option.id === data.value)?.label ?? data.value;
}

export const singlePicklistRenderer: CustomRenderer<SinglePicklistCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is SinglePicklistCell =>
    'kind' in cell.data && cell.data.kind === 'singlePicklist',
  draw: (args, cell) => {
    const observed = cell.data.observed;
    if (observed === undefined) {
      drawTextCell(args, labelOf(cell.data));
      return;
    }
    paintChips(args, [{ label: labelOf(cell.data), tone: 'neutral' }, observed]);
  },
  provideEditor: () => ({
    disablePadding: true,
    editor: (props) => (
      <PicklistEditor
        multiple={false}
        options={props.value.data.options}
        selected={[props.value.data.value]}
        onToggle={(id) =>
          props.onFinishedEditing({ ...props.value, data: { ...props.value.data, value: id } })
        }
        onCommit={() => props.onFinishedEditing(props.value)}
        onRevert={() => props.onFinishedEditing(undefined)}
      />
    ),
  }),
};

export function singlePicklistCell(
  value: string,
  options: readonly PicklistOption[],
  { editable = true, observed }: { editable?: boolean; observed?: Chip | undefined } = {},
): SinglePicklistCell {
  const data: SinglePicklistData = { kind: 'singlePicklist', value, options, observed };
  return {
    kind: GridCellKind.Custom,
    // The overlay is the editor, so a column nothing may write does not open
    // one: a picklist that opens and then refuses the choice is worse than a
    // cell that reads as fixed.
    allowOverlay: editable,
    copyData: labelOf(data),
    data,
  };
}
